import { Suspense, lazy } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "../../../_context/ToastContext/ToastContext";

const CompanyFormModal = lazy(() => import("../../../_features/company/modals/CompanyFormModal"));
const ProposalFormModal = lazy(() => import("../../../_features/proposals/modals/ProposalFormModal"));
const SubscriptionFormModal = lazy(() => import("../../../_features/subscriptions/modals/SubscriptionFormModal"));
const InvoiceFormModal = lazy(() => import("../../../_features/invoices/modals/InvoiceFormModal"));

/**
 * Replaces GlobalModals.js - renders whichever quick-create modal is active,
 * driven by `useQuickCreateModals` state instead of ModalContext. Reproduces
 * each deleted *Workflow.jsx's onSuccess logic verbatim, including the
 * don't-double-toast nuance: Company's modal doesn't toast internally so its
 * handler does; Proposal/Subscription/Invoice's underlying form hooks already
 * toast on submit, so theirs don't (see docs/bug-sheet.md).
 */
export const QuickCreateModals = ({ activeQuickCreate, onClose }) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  if (!activeQuickCreate) return null;
  const { type, props } = activeQuickCreate;

  const handleCompanySuccess = () => {
    showToast("Company created successfully!", "success");
    if (!location.pathname.includes("/company")) {
      navigate("/company");
    }
  };

  const handleProposalSuccess = () => {
    if (!location.pathname.includes("/sell/proposals")) {
      navigate("/sell/proposals");
    }
  };

  const handleSubscriptionSuccess = () => {
    if (!location.pathname.includes("/subscriptions")) {
      navigate("/subscriptions");
    }
  };

  const handleInvoiceSuccess = () => {
    if (!location.pathname.includes("/collect/invoice")) {
      navigate("/collect/invoice");
    }
  };

  return (
    <Suspense fallback={null}>
      {type === "company" && (
        <CompanyFormModal
          isOpen={true}
          onClose={onClose}
          mode={props?.mode}
          rowData={props?.rowData}
          onSuccess={handleCompanySuccess}
        />
      )}
      {type === "proposal" && (
        <ProposalFormModal
          isOpen={true}
          onClose={onClose}
          mode={props?.mode === "edit" ? "edit" : "create"}
          initialData={props?.rowData}
          customerData={props?.customerData}
          onSuccess={handleProposalSuccess}
        />
      )}
      {type === "subscription" && (
        <SubscriptionFormModal
          isOpen={true}
          onClose={onClose}
          mode={props?.mode === "edit" ? "edit" : "create"}
          initialData={props?.rowData}
          customerData={props?.customerData}
          onSuccess={handleSubscriptionSuccess}
        />
      )}
      {type === "invoice" && (
        <InvoiceFormModal
          isOpen={true}
          onClose={onClose}
          customerData={props?.customerData}
          onSuccess={handleInvoiceSuccess}
        />
      )}
    </Suspense>
  );
};
